import { TrpcAuthNotReadyError, TrpcTokenRefreshError } from './errors';

/**
 * The literal rather than either app's `logoutUrl()`, matching
 * `ApolloGraphqlProvider.getAuthHeaders` in both. Web's `logoutUrl` lives in a
 * module that imports `Parent_Type_Enum` from the generated GraphQL client,
 * which would pull the whole generated schema onto the auth path and make this
 * module unloadable without codegen having run; and neither app's routes are
 * reachable from this package anyway.
 *
 * Both apps route it: `packages/web/src/utils/urls.ts` and
 * `packages/third-party-portal/src/routes/urls.ts`. Keep in step with them if
 * the route ever moves.
 */
const LOGOUT_PATH = '/logout';

export interface LogoutRedirector {
  /** Navigates to `/logout`. Subsequent calls are no-ops. */
  redirectOnce: () => void;
}

/**
 * A `/logout` navigation that happens at most once, however many callers ask
 * for it.
 *
 * The session can end in two places and they are not mutually exclusive: the
 * request side, when a silent token refresh fails (`createTrpcAuthHeaders`
 * below, COI-897), and the response side, when the server answers 401
 * (`./authRedirectLink`, COI-159). Batching is off (`maxItems: 1`, COI-451), so
 * one query is one request and COI-882 measured 64 of them landing in a single
 * second as a page fanned out. Each app's `TrpcProvider` therefore builds ONE
 * redirector and hands it to both, so an expired session produces a single
 * navigation rather than one per request per side.
 *
 * Closure state rather than module state so it needs no reset between tests.
 */
export const createLogoutRedirector = ({
  redirectToLogout = () => {
    window.location.href = LOGOUT_PATH;
  },
}: {
  /**
   * The navigation itself. Defaults to the real one; overridden in tests, which
   * cannot navigate the runner.
   */
  redirectToLogout?: () => void;
} = {}): LogoutRedirector => {
  let hasRedirectedToLogout = false;

  return {
    redirectOnce: () => {
      if (hasRedirectedToLogout) {
        return;
      }

      hasRedirectedToLogout = true;
      redirectToLogout();
    },
  };
};

interface TrpcAuthHeadersOptions {
  /** Auth0 `isLoading` — read at call time, not captured. */
  isLoading: () => boolean;
  /** Auth0 `isAuthenticated` — read at call time, not captured. */
  isAuthenticated: () => boolean;
  /** Auth0 `getAccessTokenSilently` — read at call time, not captured. */
  getToken: () => Promise<string>;
  /**
   * Drives re-authentication after a failed silent refresh. Defaults to the
   * real navigation; overridden in tests, which cannot navigate the runner.
   *
   * Ignored when `redirector` is supplied — the redirector owns its own
   * navigation.
   */
  redirectToLogout?: () => void;
  /**
   * The `/logout` navigation shared with the response-side link, so the two
   * cannot both fire. Defaults to a private one wrapping `redirectToLogout`.
   */
  redirector?: LogoutRedirector;
}

/**
 * Builds the `headers()` callback for the tRPC links in either app's
 * `TrpcProvider`.
 *
 * Extracted from the provider so its branch selection is unit-testable without
 * React — the same reason `graphqlErrorHandler.ts` was lifted out of
 * `ApolloGraphqlProvider` (RSP-6971).
 *
 * **Throws rather than returning `{}` (COI-897).** Both branches used to return
 * an empty header object, so the request went out with no `Authorization`
 * header and `express-jwt` answered 401 — 159 of them in 48h on web alone, with
 * no re-auth prompt for the user and no way to tell the two causes apart.
 * `@trpc/client` awaits `headers()` *before* it calls `fetch`, so throwing
 * aborts the request outright instead of sending one that cannot succeed.
 * Nothing legitimate is lost: `expressjwt(...).unless({ path: ['/healthz',
 * '/version'] })` guards every `/trpc/*` route
 * (`packages/trpc/src/trpc-server.ts`), and no router uses `publicProcedure`.
 */
export const createTrpcAuthHeaders = ({
  isLoading,
  isAuthenticated,
  getToken,
  redirectToLogout,
  redirector = createLogoutRedirector({ redirectToLogout }),
}: TrpcAuthHeadersOptions) => {
  return async (): Promise<Record<string, string>> => {
    // Auth0 has not settled yet, so no token can exist. Deliberately does NOT
    // redirect: the login flow itself runs in this state, and React Query's
    // default retries pick the request up again once auth resolves.
    if (isLoading() || !isAuthenticated()) {
      throw new TrpcAuthNotReadyError();
    }

    try {
      return { Authorization: `Bearer ${await getToken()}` };
    } catch (error) {
      // The session can no longer be renewed silently. Redirect to /logout —
      // the same re-auth mechanism the GraphQL path uses (RSP-4478,
      // ApolloGraphqlProvider.getAuthHeaders). Apollo and tRPC are mounted
      // together during the migration, so a second, different mechanism here
      // (the ticket suggested `loginWithRedirect`) would race Apollo's.
      redirector.redirectOnce();

      throw new TrpcTokenRefreshError({ cause: error });
    }
  };
};
